/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { useUniqueId } from '@squiz/dxp-console-support';
import {
  ColumnDef,
  RowSelectionState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { isUndefined } from 'lodash';
import React, { LegacyRef, forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { ICON_CHEVRON_LEFT, ICON_CHEVRON_RIGHT } from '../../Icons';
import { FormControl, FormControlProps } from '../FormFields/FormControl';
import { SdsIcon } from '../SdsIcon';
import { SPINNER_TYPE_REGULAR, Spinner } from '../Spinner';
import { TableCell } from '../Table';
import { ApiTableProps, apiTableDefaults } from './ApiTable.props';
import {
  API_TABLE_EVENT_LOADING,
  API_TABLE_EVENT_PAGE_NEXT,
  API_TABLE_EVENT_PAGE_PREVIOUS,
  API_TABLE_EVENT_PAGE_SIZE,
  ApiTableEvent,
} from './types/ApiTableEvent.types';

/**
 * SDS API Table component.
 */
export const ApiTable = forwardRef((props: ApiTableProps, ref: React.Ref<unknown>): JSX.Element => {
  const {
    cacheKey,
    classes,
    emptyText,
    fetchData,
    initialFetch,
    isSelectable,
    isPaginated = true,
    onRowSelect,
    onRowDoubleClick,
    isSlim,
    loading,
    noMoreText,
    pageTokens,
    perPageDefault,
    perPageOptions,
    perPageText,
    tableContent,
    ...rest
  } = {
    ...apiTableDefaults,
    ...props,
  };
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [shouldFetch, setShouldFetch] = useState<boolean>(isUndefined(initialFetch) ? true : initialFetch);
  const [fetchType, setFetchType] = useState<ApiTableEvent>(API_TABLE_EVENT_LOADING);
  const formControlRef = useRef<HTMLInputElement>(null);
  const uniqueId = useUniqueId();
  const tableId = `sds-table--${uniqueId}`;
  const tableClass = clsx(`sds-table__wrapper`, `sds-table--api`, classes, {
    [`table--slim`]: isSlim,
  });
  const tableRows = useMemo((): Array<{}> => {
    return tableContent.rows;
  }, [tableContent]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const tableColumns = useMemo((): Array<ColumnDef<unknown, any>> => {
    return tableContent.columns.map((column: TableCell) => {
      return {
        accessorKey: column.key,
        header: () => column.content,
        id: column.actionable ? `${column.key}--actionable` : column.key,
      };
    });
  }, [tableContent.columns]);

  const tableInstance = useReactTable({
    columns: tableColumns,
    data: tableRows,
    enableRowSelection: isSelectable,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: perPageDefault,
      },
    },
    manualPagination: true,
    onRowSelectionChange: setSelectedRows,
    pageCount: -1,
    state: {
      rowSelection: selectedRows,
    },
  });

  const {
    getHeaderGroups,
    getRowModel,
    getRowCount,
    getCanNextPage,
    getCanPreviousPage,
    getIsAllRowsSelected,
    toggleAllRowsSelected,
    nextPage,
    previousPage,
    setPageSize,
    getState,
  } = tableInstance;
  const {
    pagination: { pageIndex, pageSize },
  } = getState();

  useEffect(() => {
    if (!onRowSelect) {
      return;
    }
    const selectedData = tableInstance
      .getRowModel()
      .flatRows.filter((row) => row.getIsSelected())
      .map((row) => row.original);

    onRowSelect(selectedData);
  }, [onRowSelect, pageIndex, pageSize, selectedRows, tableInstance]);

  // Next/previous conditions
  let nextDisabled = false;
  let previousDisabled = false;

  if (loading) {
    nextDisabled = true;
    previousDisabled = true;
  } else if (pageTokens) {
    nextDisabled = !pageTokens.pageAfter;
    previousDisabled = !pageTokens.pageBefore;
  } else {
    nextDisabled = !getCanNextPage();
    previousDisabled = !getCanPreviousPage();
  }

  // Apply table pagination
  const tableFooter = (
    <div className="sds-table__pagination">
      <div className="sds-table__pagination-options">
        <span className="sds-table__per-page">{perPageText}</span>
        <select
          aria-label="Select number of rows per page"
          className="sds-table__page-select"
          disabled={loading}
          onChange={(e): void => {
            setPageSize(Number(e.target.value));
            setFetchType(API_TABLE_EVENT_PAGE_SIZE);
            toggleAllRowsSelected(false);
            setShouldFetch(true);
          }}
          value={pageSize}
        >
          {perPageOptions.map((pageSize) => (
            <option key={pageSize} value={pageSize}>
              {pageSize}
            </option>
          ))}
        </select>
      </div>
      <div className="sds-table__navigator">
        <button
          aria-label="Previous"
          disabled={previousDisabled}
          onClick={(): void => {
            previousPage();
            toggleAllRowsSelected(false);
            setFetchType(API_TABLE_EVENT_PAGE_PREVIOUS);
            setShouldFetch(true);
          }}
        >
          <SdsIcon svg={ICON_CHEVRON_LEFT} />
        </button>
        <button
          aria-label="Next"
          disabled={nextDisabled}
          onClick={(): void => {
            nextPage();
            toggleAllRowsSelected(false);
            setFetchType(API_TABLE_EVENT_PAGE_NEXT);
            setShouldFetch(true);
          }}
        >
          <SdsIcon svg={ICON_CHEVRON_RIGHT} />
        </button>
      </div>
    </div>
  );

  useImperativeHandle(ref, () => ({
    clearSelection: (): void => {
      setSelectedRows({});
      toggleAllRowsSelected(false);
    },
  }));

  const getTableHeaders = (): Array<JSX.Element> => {
    const headerGroups = getHeaderGroups();
    return headerGroups.map((headerGroup) => {
      return (
        <tr key={headerGroup.id}>
          {headerGroup.headers.map((header) => {
            if (header.index === 0 && isSelectable) {
              return (
                <th className="sds-table__th--selectable" key={header.id} onClick={() => toggleAllRowsSelected()}>
                  <div className="sds-table__th-content">
                    <FormControl
                      checked={getIsAllRowsSelected()}
                      fieldLabel={header.column.id}
                      hideLabel
                      id={header.column.id}
                      name={`table-header--${header.column.id}`}
                      option={{
                        key: header.column.id,
                        label: '',
                      }}
                      tabIndex={0}
                      type="checkbox"
                    />
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </div>
                </th>
              );
            }
            return (
              <th
                aria-label={`${flexRender(header.column.columnDef.header, header.getContext())}`}
                aria-sort="none"
                key={header.id}
              >
                <div className="sds-table__th-content">
                  {flexRender(header.column.columnDef.header, header.getContext())}
                </div>
              </th>
            );
          })}
        </tr>
      );
    });
  };

  const getTableRows = (): Array<JSX.Element> => {
    const { rows } = getRowModel();

    // Handle page with no items
    if (!getRowCount()) {
      return [
        <tr className="sds-table__empty-row" key={`${tableId}--empty-row`}>
          <td>{pageIndex !== 0 ? noMoreText : emptyText}</td>
        </tr>,
      ];
    }

    // Get rows for this page
    return rows.map((row) => {
      // Look for custom properties
      let customClass = ``;
      const originalRows = row.original;

      if (originalRows) {
        if (Object.keys(originalRows).indexOf(`_config`) !== -1) {
          // We know the _config property exists on the provided custom object
          // @ts-ignore TS2339
          const config = originalRows._config as RowConfig;
          customClass = config.classes;
        }
      }

      const constructFormControl = (): JSX.Element => {
        const formControlProps: FormControlProps = {
          checked: row.getIsSelected(),
          fieldLabel: row.id,
          hideLabel: true,
          id: row.id,
          key: row.id,
          name: `table-row--${row.id}`,
          option: {
            key: row.id,
            label: '',
          },
          tabIndex: 0,
          type: 'checkbox',
        };

        return <FormControl {...formControlProps} ref={formControlRef} />;
      };

      return (
        <tr
          className={clsx(customClass, {
            ['sds-table__row--selectable']: isSelectable,
            [`sds-table__row--selected`]: row.getIsSelected(),
          })}
          data-id={(row.original as { rowId: string }).rowId}
          key={row.id}
          onClick={(e: React.MouseEvent) => {
            // If cell is marked as actionable, don't trigger row selection on clicking contents of the cell.
            // Workaround, as e.stopPropagation() on children doesn't fully cover the use case.
            const target = e.target as HTMLElement;
            if (target.closest('.actionable') && !target.classList.contains('actionable')) {
              return;
            }

            if (isSelectable) {
              row.toggleSelected();
            }
          }}
          onDoubleClick={
            onRowDoubleClick
              ? (e): void => {
                  onRowDoubleClick(e);
                }
              : undefined
          }
        >
          {row.getAllCells().map((cell, i) => {
            const isActionable = cell.getContext().column.id.includes('actionable');
            if (i === 0 && isSelectable) {
              return (
                <td key={cell.id}>
                  <div className="checkbox-cell">
                    {constructFormControl()}
                    {cell.renderValue() as number | string | JSX.Element}
                  </div>
                </td>
              );
            }
            return (
              <td className={clsx([{ actionable: isActionable }])} key={cell.id}>
                {cell.renderValue() as number | string | JSX.Element}
              </td>
            );
          })}
        </tr>
      );
    });
  };

  // Loader that appears inside the table when new data for another page is being fetched
  const getTableRefreshLoader = (): JSX.Element => {
    return (
      <tr className="sds-table__loader-row">
        <td className="loading"></td>
      </tr>
    );
  };

  // Listen for trigger value and fetch the new data
  useEffect(() => {
    if (shouldFetch) {
      const options = {
        eventType: fetchType,
        pageIndex,
        pageSize,
      };
      fetchData(options);
      setShouldFetch(false);
    }
  }, [fetchData, fetchType, pageIndex, pageSize, shouldFetch]);

  // Listen for cache key and fetch first page
  useEffect(() => {
    if (cacheKey) {
      const options = {
        eventType: API_TABLE_EVENT_PAGE_SIZE as ApiTableEvent,
        pageIndex: 1,
        pageSize: 20,
      };
      fetchData(options);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  // Initial loading state (no table visible)
  // These conditions ensure it only occurs on initial page load
  if (loading && !getCanPreviousPage() && tableRows.length === 0) {
    return (
      <div className="sds-table sds-table--loading">
        <Spinner type={SPINNER_TYPE_REGULAR} />
      </div>
    );
  }

  return (
    <div className={tableClass} {...rest} ref={ref as LegacyRef<HTMLDivElement>}>
      <div className="sds-table">
        <table id={tableId}>
          <thead>{getTableHeaders()}</thead>
          <tbody>
            {loading && getTableRefreshLoader()}
            {getTableRows()}
          </tbody>
        </table>
      </div>
      {isPaginated && tableFooter}
    </div>
  );
});
